import React, { useEffect, useRef, useState } from "react";
import { createSupportRequest, listMySupportRequests } from "../../api";

const CATEGORIES = [
  ["issue", "Report an issue", "Something is broken or not working as expected."],
  ["feedback", "Give feedback", "Tell us what you think of the app."],
  ["feature", "Request a feature", "Suggest something you'd like us to build."],
];

const CATEGORY_LABELS = { issue: "Issue", feedback: "Feedback", feature: "Feature request" };

const inputClass =
  "w-full px-3.5 py-2.5 rounded-xl border border-parchment-300 bg-white/80 text-base text-ink-800 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition";

function StatusPill({ status }) {
  const resolved = status === "resolved";
  return (
    <span
      className={
        "text-[11px] font-semibold px-2 py-0.5 rounded-full " +
        (resolved
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : "bg-amber-50 text-amber-700 border border-amber-200")
      }
    >
      {resolved ? "Resolved" : "Open"}
    </span>
  );
}

/**
 * HelpPanel — the modal behind the floating "?" button. Two tabs:
 * a contact-support form and the caller's past requests with any admin
 * responses (which are also emailed).
 *
 * Props: onClose, currentUser (for the confirmation copy).
 */
export default function HelpPanel({ onClose, currentUser }) {
  const [tab, setTab] = useState("new"); // "new" | "mine"

  // New-request form state
  const [category, setCategory] = useState("issue");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const subjectRef = useRef(null);

  // My-requests state
  const [requests, setRequests] = useState(null); // null = loading
  const [listError, setListError] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (tab === "new") subjectRef.current?.focus();
  }, [tab]);

  useEffect(() => {
    if (tab !== "mine") return;
    setListError(null);
    setRequests(null);
    listMySupportRequests()
      .then(setRequests)
      .catch((e) => setListError(e.message || "Failed to load your requests"));
  }, [tab]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!subject.trim()) {
      setError("Please add a short subject.");
      return;
    }
    if (!message.trim()) {
      setError("Please describe your issue or idea.");
      return;
    }
    setBusy(true);
    try {
      await createSupportRequest({
        category,
        subject: subject.trim(),
        message: message.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || "Failed to send your request.");
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setDone(false);
    setSubject("");
    setMessage("");
    setCategory("issue");
    setError(null);
  };

  const tabClass = (active) =>
    "px-3 py-1.5 rounded-lg text-sm font-semibold transition " +
    (active ? "bg-parchment-200 text-ink-800" : "text-ink-400 hover:text-ink-700");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink-800">Help &amp; support</h2>
            <p className="text-xs text-ink-400">
              Report an issue, share feedback, or request a feature.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 shrink-0">
          <button className={tabClass(tab === "new")} onClick={() => setTab("new")}>
            New request
          </button>
          <button className={tabClass(tab === "mine")} onClick={() => setTab("mine")}>
            My requests
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto scroll-elegant">
          {tab === "new" &&
            (done ? (
              <div className="text-center py-6">
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" role="status">
                  Sent — thank you! We'll reply to{" "}
                  <span className="font-semibold">{currentUser?.email || "your account email"}</span>, and
                  you can also check{" "}
                  <button className="underline font-semibold" onClick={() => setTab("mine")}>
                    My requests
                  </button>
                  .
                </p>
                <button
                  onClick={resetForm}
                  className="mt-4 text-sm text-ink-400 hover:text-ink-700 underline"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  {CATEGORIES.map(([value, label, hint]) => (
                    <label
                      key={value}
                      className={
                        "flex items-start gap-2.5 rounded-xl px-3 py-2 cursor-pointer border transition " +
                        (category === value
                          ? "border-[var(--gold)] bg-parchment-100"
                          : "border-parchment-200 hover:border-parchment-300")
                      }
                    >
                      <input
                        type="radio"
                        name="support-category"
                        className="mt-1"
                        checked={category === value}
                        onChange={() => setCategory(value)}
                      />
                      <span>
                        <span className="block text-sm font-semibold text-ink-800">{label}</span>
                        <span className="block text-xs text-ink-400">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <input
                  ref={subjectRef}
                  type="text"
                  className={inputClass}
                  placeholder="Subject"
                  maxLength={200}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <textarea
                  className={inputClass}
                  rows={5}
                  placeholder="Describe the issue, feedback, or feature…"
                  maxLength={4000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                {error && (
                  <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink font-semibold text-sm hover:brightness-105 disabled:opacity-50 shadow-soft transition-colors"
                >
                  {busy ? "Sending…" : "Send to support"}
                </button>
              </form>
            ))}

          {tab === "mine" && (
            <div className="flex flex-col gap-3">
              {listError && (
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {listError}
                </p>
              )}
              {requests === null && !listError && (
                <p className="text-sm text-ink-400 py-4 text-center">Loading…</p>
              )}
              {requests?.length === 0 && (
                <p className="text-sm text-ink-400 py-4 text-center">
                  You haven't raised any requests yet.
                </p>
              )}
              {requests?.map((r) => (
                <div key={r.id} className="border border-parchment-200 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-ink-400 uppercase tracking-wide">
                      {CATEGORY_LABELS[r.category] || r.category}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-ink-300">
                        {new Date(r.created_at).toLocaleDateString()}
                      </span>
                      <StatusPill status={r.status} />
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-ink-800 mt-1">{r.subject}</p>
                  <p className="text-sm text-ink-500 mt-1 whitespace-pre-wrap">{r.message}</p>
                  {r.response && (
                    <div className="mt-2 ms-3 border-s-2 border-[var(--gold)] ps-3">
                      <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wide">
                        Response from support
                        {r.responded_at
                          ? ` · ${new Date(r.responded_at).toLocaleDateString()}`
                          : ""}
                      </p>
                      <p className="text-sm text-ink-700 mt-0.5 whitespace-pre-wrap">{r.response}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
